import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { dasherize } from '@ember/string';
import { isEmpty } from '@ember/utils';
import { task, taskGroup, waitForProperty } from 'ember-concurrency';
import { task as trackedTask } from 'reactiveweb/ember-concurrency';
import { isDevelopingApp, isTesting, macroCondition } from '@embroider/macros';
import { forEach, filter, pipe, without, pluck } from 'ramda';
import { intoArray } from '@eflexsystems/ramda-helpers';
import { isAncestorOf, recursiveSave } from 'eflex/util/tree-helpers';
import JobStatuses from 'eflex/constants/kinetic-job-statuses';
import { copyTask, addToEndOfSequence } from 'eflex/services/task-repo';
import ConfigurationHistoryTypes from 'eflex/constants/configuration-history-types';

const filterKineticDeleted = (data) => {
  const options = data.filter(d => !d.kineticDeleted);
  const deleted = data.filter(d => d.kineticDeleted);

  if (!isEmpty(deleted)) {
    options.push({
      groupName: 'Deleted',
      options: deleted,
    });
  }

  return options;
};

export default class KineticOperationsOperationController extends Controller {
  @tracked showConfigurationHistoryNotesModal = false;
  @tracked showTaskPanel = false;
  @tracked activeTaskPanelTab = 'task-device';
  @tracked operation;
  @tracked selectedTask;
  @tracked _selectedPart;
  @tracked _selectedPartRev;
  @tracked showCopyOperationModal = false;
  @tracked showCopyWarningModal = false;
  @tracked pendingCopyArgs = null;
  @tracked activeAssemblyOps;

  gridTaskConfigLoadTracker = new Map();
  operationConfigurationHistoryType = ConfigurationHistoryTypes.OPERATION;

  @service taskRepo;
  @service validationErrorNotifier;
  @service notifier;
  @service systemConfig;
  @service store;
  @service currentUser;
  @service eventBus;
  @service taskConfigRepo;
  @service kineticOperationRepo;
  @service kineticRepo;
  @service fileDownloader;
  @service router;

  @taskGroup({ drop: true }) operationActions;

  loadAssemblyOps = task(waitFor(async (part) => {
    this.activeAssemblyOps = await this.store.query('kinetic-assembly-operation', {
      operations: pluck('id', part.operations),
      jobStatus: JobStatuses.released,
    });
  }));

  get showSpinner() {
    return this.copyTask.isRunning ||
      this.handleCopyOperation.isRunning ||
      (this.save.isRunning && !this.showConfigurationHistoryNotesModal);
  }

  get hasSelected() {
    return this.selectedPart || this.selectedPartRev || this.operation;
  }

  get selectedPart() {
    return this._selectedPart ?? this.operation?.part;
  }

  set selectedPart(val) {
    this._selectedPart = val;
  }

  get selectedPartRev() {
    return this._selectedPartRev ?? this.operation?.partRev;
  }

  set selectedPartRev(val) {
    this._selectedPartRev = val;
  }

  get filteredParts() {
    const parts = this.kineticRepo.parts.filter(part => {
      return isEmpty(part.sourceJobNumber);
    }) ?? [];

    return filterKineticDeleted(parts);
  }

  get filteredPartRevs() {
    const partRevs = this.kineticRepo.partRevs.filter(partRev => {
      return partRev.parent === this.selectedPart && isEmpty(partRev.sourceJobNumber);
    }) ?? [];

    return filterKineticDeleted(partRevs);
  }

  get filteredOperations() {
    const operations = this.kineticOperationRepo.operations.filter(operation => {
      return operation.parent === this.selectedPartRev && isEmpty(operation.sourceJobNumber);
    }) ?? [];

    return filterKineticDeleted(operations);
  }

  _loadTaskGridConfigs = task(waitFor(async (operation, modelId) => {
    const cacheKey = modelId ?? true;
    const operationCacheSet = this.gridTaskConfigLoadTracker.get(operation.id);

    if (operationCacheSet?.has(cacheKey)) {
      return;
    }

    if (operationCacheSet) {
      operationCacheSet.add(cacheKey);
    } else {
      this.gridTaskConfigLoadTracker.set(operation.id, new Set([cacheKey]));
    }

    await this.store.query('task-config', {
      ancestorPath: operation.path,
      model: modelId,
    });
  }));

  gridTaskConfigs = trackedTask(this, this._loadTaskGridConfigs, () => [
    this.operation,
  ]);

  get isInvalid() {
    return this.operation?.isSelfOrChildInvalid;
  }

  get disabled() {
    return this.currentUser.isNotAdmin || this.operationActions.isRunning;
  }

  get isDirty() {
    if (this.operation?.isDeleted) {
      return false;
    }

    return this.operation?.isSelfOrChildDirty ?? false;
  }

  save = task({ group: 'operationActions' }, waitFor(async () => {
    if (!this.operation) {
      return;
    }

    if (this.isInvalid) {
      this.validationErrorNotifier.sendErrors([this.operation]);
      return;
    }

    const buildStatuses = await this.store.query('live-build-status', {
      operation: this.operation.id,
      limit: 1,
    });

    if (macroCondition(!isDevelopingApp() || isTesting())) {
      // eslint-disable-next-line unicorn/no-lonely-if
      if (!isEmpty(buildStatuses)) {
        this.notifier.sendError('logs.operationIsRunning');
        return;
      }
    }

    if (this.systemConfig.jem?.configurationHistory) {
      this.showConfigurationHistoryNotesModal = true;
      await waitForProperty(this, 'showConfigurationHistoryNotesModal', false);
    }

    await recursiveSave(this.operation);
  }));

  copyTask = task({ group: 'operationActions' }, waitFor(async treeTask => {
    if (!this.operation || this.currentUser.isNotAdmin) {
      return;
    }

    await this.taskConfigRepo.loadTaskConfigs.perform(treeTask);

    const copy = copyTask(treeTask, this.operation);

    this.closeAllPanels();
    Object.assign(this, {
      activeTaskPanelTab: 'task-device',
      showTaskPanel: true,
      selectedTask: copy,
    });
  }));

  handleCopyOperation = task({ group: 'operationActions' }, waitFor(async (targetOperation, copyFromOperation) => {
    this.pendingCopyArgs = { targetOperation, copyFromOperation };

    if (this.operation.hasTaskMaterials || targetOperation.hasTaskMaterials) {
      this.showCopyWarningModal = true;
      this.showCopyOperationModal = false;
      return;
    }

    await this.performCopy.linked().perform(targetOperation, copyFromOperation);
  }));

  confirmCopyWarning = task({ group: 'operationActions' }, waitFor(async () => {
    const { targetOperation, copyFromOperation } = this.pendingCopyArgs;
    this.showCopyWarningModal = false;
    await this.performCopy.linked().perform(targetOperation, copyFromOperation);
  }));

  performCopy = task(waitFor(async (targetOperation, copyFromOperation) => {
    const sourceOp = copyFromOperation ? this.operation : targetOperation;
    const destinationOp = copyFromOperation ? targetOperation : this.operation;

    await this.kineticOperationRepo.copyOperationTasks.perform(sourceOp, destinationOp);
    this.showCopyOperationModal = false;
  }));

  exportPdf = task({ drop: true }, waitFor(async () => {
    await this.fileDownloader.getFile.perform(
      `operationWorkInstructions/${this.operation.id}`,
      'application/pdf, application/zip',
      `operation_instructions_${dasherize(this.operation.name)}`,
    );
  }));

  @action
  updateTaskCardRows(treeItem) {
    if (treeItem.type !== 'task' || this.operation == null || !isAncestorOf(this.operation, treeItem)) {
      return;
    }

    const newTaskStart = treeItem.row;
    const newTaskEnd = newTaskStart + treeItem.sizeY;

    pipe(
      intoArray(
        without([treeItem]),
        filter(taskRecord => {
          const changed = taskRecord.changedAttributes();
          return changed.row != null && newTaskStart <= changed.row[1] && changed.row[1] < newTaskEnd;
        }),
      ),
      forEach(taskRecord => {
        addToEndOfSequence(this.operation, taskRecord);
      }),
    )(this.operation.tasks);
  }

  closeAllPanels() {
    this.showTaskPanel = false;
  }

  openTaskPanel = task(waitFor(async taskRecord => {
    Object.assign(this, {
      showTaskPanel: true,
    });

    await this.taskConfigRepo.loadTaskConfigs.perform(taskRecord);

    this.selectedTask = taskRecord;
  }));

  @action
  deleteTask(taskRecord) {
    if (this.selectedTask === taskRecord) {
      Object.assign(this, {
        selectedTask: null,
        showTaskPanel: false,
      });
    }

    this.taskRepo.delete(taskRecord);
  }

  @action
  rollback() {
    if (this.selectedTask?.isNew) {
      this.closeAllPanelsAndClearPanelModel();
    }

    this.operation.rollbackAttributes();
    this.eventBus.trigger('taskCardsRolledBack');
  }

  @action
  addTask(taskType) {
    this.closeAllPanels();
    Object.assign(this, {
      showTaskPanel: true,
      activeTaskPanelTab: 'task-device',
      selectedTask: this.taskRepo.create({
        parent: this.operation,
        taskType,
        row: (this.selectedTask?.row ? this.selectedTask.rowEnd + 1 : null),
        column: 1,
      }),
    });
  }

  @action
  closeAllPanelsAndClearPanelModel() {
    this.closeAllPanels();
    this.selectedTask = null;
    this.activeTaskPanelTab = 'task-device';
  }
}
